import { useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { addSiteComment, addReaction } from '../../firebase/siteDiscussions';

export const useCommentActions = (siteId, user, comments, setComments, fetchComments) => {
  const toast = useToast();

  const handleLike = useCallback(async (commentId, isLiked) => {
    if (!user) {
      toast({
        title: 'Please sign in to like comments',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const action = isLiked ? 'unlike' : 'like';
      const result = await addReaction(siteId, commentId, user.uid, action);
      
      if (result.success) {
        setComments(prevComments => 
          prevComments.map(comment => {
            if (comment.id === commentId) {
              const likes = [...(comment.likes || [])];
              if (isLiked) {
                const index = likes.indexOf(user.uid);
                if (index > -1) likes.splice(index, 1);
              } else {
                likes.push(user.uid);
              }
              return { ...comment, likes };
            } else if (comment.replies?.some(reply => reply.id === commentId)) {
              return {
                ...comment,
                replies: comment.replies.map(reply => {
                  if (reply.id === commentId) {
                    const likes = [...(reply.likes || [])];
                    if (isLiked) {
                      const index = likes.indexOf(user.uid);
                      if (index > -1) likes.splice(index, 1);
                    } else {
                      likes.push(user.uid);
                    }
                    return { ...reply, likes };
                  }
                  return reply;
                })
              };
            }
            return comment;
          })
        );
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error updating like:', error);
      toast({
        title: 'Error updating like',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [siteId, user, setComments, toast]);

  const addComment = useCallback(async (commentText, photoUrl) => {
    if (!user) {
      toast({
        title: 'Please sign in to comment',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!commentText.trim() && !photoUrl) {
      toast({
        title: 'Comment cannot be empty',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    try {
      if (!user.displayName) {
        toast({
          title: 'Profile incomplete',
          description: 'Please set your display name in your profile before commenting',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }

      const result = await addSiteComment(siteId, {
        text: commentText.trim(),
        userId: user.uid,
        userName: user.displayName,
        authorName: user.displayName,
        photoUrl: photoUrl
      });

      if (result.success) {
        await fetchComments();
        toast({
          title: 'Comment added',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        return true;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: 'Error adding comment',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [siteId, user, fetchComments, toast]);

  const addReply = useCallback(async (replyText, replyPhotoUrl, replyTo) => {
    if (!user) {
      toast({
        title: 'Please sign in to reply',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!replyText.trim() && !replyPhotoUrl) {
      toast({
        title: 'Reply cannot be empty',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    try {
      if (!user.displayName) {
        toast({
          title: 'Profile incomplete',
          description: 'Please set your display name in your profile before replying',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }

      const findTopLevelParent = (replyId) => {
        const parentComment = comments.find(c => 
          c.id === replyId || c.replies.some(r => r.id === replyId)
        );
        return parentComment ? parentComment.id : replyId;
      };

      const originalParentId = findTopLevelParent(replyTo);
      
      const result = await addSiteComment(siteId, {
        text: replyText.trim(),
        userId: user.uid,
        userName: user.displayName,
        authorName: user.displayName,
        photoUrl: replyPhotoUrl,
        replyTo: replyTo,
        originalParentId,
        type: 'reply'
      });

      if (result.success) {
        await fetchComments();
        toast({
          title: 'Reply added',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        return true;
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error adding reply:', error);
      toast({
        title: 'Error adding reply',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
  }, [siteId, user, comments, fetchComments, toast]);

  return {
    handleLike,
    addComment,
    addReply
  };
};
